import { Visibility } from '@fullstory/proto/fs/gcloud/dstore/protoauditstore';
import {
  Card,
  Dashboard,
  DxCardFormat,
  DxChartType,
  FunnelMetricType,
  GetAllDashboardsResponse,
  GetCardResponse,
  GetInitialDashboardsResponse,
  Metadata,
} from '@fullstory/proto/fs/services/dx-objects';
import { Timestamp, int64, timestamp } from '@fullstory/proto/google/protobuf';

interface MockDashboardParams {
  id?: int64;
  name?: string;
  isCanned?: boolean;
  description?: string;
  cards?: int64[];
  metadata?: Metadata | null;
  lastViewed?: timestamp | null;
  isWip?: boolean;
  starred?: boolean;
  unnamedOverlayId?: string;
  visibility?: Visibility;
}

export const mockDashboard = ({
  id = int64.fromNumber(12345),
  name = 'dashboard',
  isCanned = false,
  description = '',
  cards = [int64.fromNumber(12345)],
  metadata = null,
  lastViewed = null,
  isWip = false,
  starred = false,
  unnamedOverlayId = '',
  visibility = Visibility.PUBLIC,
}: MockDashboardParams): Dashboard => ({
  id,
  name,
  isCanned,
  description,
  cards,
  metadata,
  lastViewed,
  isWip,
  starred,
  unnamedOverlayId,
  visibility,
  cardObjects: [],
});

export const mockCard: Card = {
  id: int64.fromNumber(12345),
  name: 'Test Card',
  description: 'This is a test card',
  metadata: null,
  isCanned: false,
  format: DxCardFormat.FORMAT_INVALID,
  chartType: DxChartType.CHART_TYPE_INVALID,
  funnelMetricType: FunnelMetricType.FUNNEL_METRIC_TYPE_INVALID,
};

/**
 * A helper for mocking dashboards. Right now all dashboards will have a single mocked card
 * with a card id of `12345`. A good future update would be to extend this to mock multiple cards
 * or take an array of cards to add to all dashboards.
 *
 * @param numberOfDashboards The number of dashboards to mock. Will always return at least one Dashboard.
 * @param visibility The visibility of all dashboards.
 * @returns An array of Dashboards
 */
export const mockDashboards = (
  numberOfDashboards: number = 10,
  visibility: Visibility = Visibility.PUBLIC,
): Dashboard[] => {
  const testDashboards: Dashboard[] = [];

  // Also including objects with long names to test ellipsizing
  testDashboards.push(
    mockDashboard({
      name: 'test dashboard with a very very very very very very very very very long name',
      id: 'testDashboard' as int64,
      visibility,
    }),
  );
  for (let i = 1; i < numberOfDashboards; i++) {
    const dashboardId = `testDashboard${i}` as int64;
    // Mocking half with no last modified date to test Last Modified column values
    // Mocking a third as starred to test filtering by starred
    testDashboards.push(
      mockDashboard({
        name: `Test Dashboard ${i}`,
        metadata: {
          updatedAt: i % 2 === 0 ? Timestamp.now() : null,
          createdAt: Timestamp.now(),
          createdBy: 'kristinmorgan@fullstory.com',
          updatedBy: 'kristinmorgan@fullstory.com',
          version: 0,
        },
        id: dashboardId,
        starred: i % 3 === 0 ? true : false,
        lastViewed: i % 2 === 0 ? Timestamp.now() : null,
        visibility,
      }),
    );
  }

  return testDashboards;
};

export const DASHBOARD_RESPONSE: GetAllDashboardsResponse = {
  dashboards: [],
};

export const GET_ALL_DASHBOARDS_RESPONSE = (
  numberOfDashboards: number = 10,
): GetAllDashboardsResponse => ({ dashboards: mockDashboards(numberOfDashboards) });

export const INITIAL_DASHBOARDS_RESPONSE: GetInitialDashboardsResponse = {
  starred: [],
  recent: [],
};

export const CARD_RESPONSE: GetCardResponse = {
  dashboard: mockDashboard({}),
  card: mockCard,
};
